import * as UI from "@iyk/ui"
import * as Carousel from "./ui/carousel.tsx"

import { SectionLayout } from "./ui/section-layout.tsx"

import { each } from "@iyk/flow"

// #region Root

const Root = ({ children, className, ...props }: UI.HTMLElementProps) => {
  return (
    <SectionLayout
      size="container"
      theme="dark"
      className={classForPackageDetails({ className })}
      {...props}
    >
      <div className={classForPackageDetailGrid()}>{children}</div>
    </SectionLayout>
  )
}

const classForPackageDetails = UI.cva(["w-full"])

const classForPackageDetailGrid = UI.cva([
  "md:[&>div]:py-24 md:bshadow-y md:bshadow-gray-6",
  "grid grid-cols-1 gap-14 md:gap-0 md:grid-cols-[1.3fr_0.7fr]",
])

// #endregion

// #region Parts

const Parts = ({ slides }: { slides: PackageSlide[] }) => {
  return (
    <Carousel.Root
      options={CAROUSEL_OPTIONS}
      className="flex flex-col items-center justify-center gap-6 px-0 md:px-4 md:bshadow-r md:bshadow-gray-6"
    >
      <Carousel.Slides>
        {each(slides, (slide) => (
          <Carousel.Slide
            key={slide.title}
            className="flex flex-col items-center gap-4 flex-[0_0_58%] md:flex-[0_0_33.3%]"
          >
            <img
              src={slide.image.src}
              alt={slide.image.alt}
              width={slide.image.width}
              height={slide.image.height}
              loading="lazy"
            />
            <UI.Text as="span" size="xs-upper" className="text-gray-10">
              {slide.title}
            </UI.Text>
          </Carousel.Slide>
        ))}
      </Carousel.Slides>
      <Carousel.Controls className="gap-2" />
    </Carousel.Root>
  )
}

// #endregion

// #region Overview

const Overview = ({
  parts,
  specifications,
}: {
  parts: PackageParts
  specifications: Specifications
}) => {
  return (
    <div className="flex flex-col m-auto gap-10 md:max-w-80 px-4">
      <div className="flex flex-col gap-6">
        <span className="text-xs-upper text-white text-center">{parts.title}</span>
        <div className={classForPackageDetailText()}>
          {each(parts.items, (item) => (
            <p key={item.description} className="text-sm text-gray-10">
              {item.description}
            </p>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-6">
        <span className="text-xs-upper text-white text-center">{specifications.title}</span>
        <div className={classForPackageDetailText()}>
          {each(specifications.items, (item) => (
            <div key={item.title} className="flex items-center justify-between">
              <p className="text-sm text-gray-10">{item.title}</p>
              <p className="text-sm text-gray-10">{item.value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const classForPackageDetailText = UI.cva([
  "flex flex-col gap-4",
  "text-gray-11 text-sm",
  "[&>:not(:last-child)]:pb-4",
  "[&>:not(:last-child)]:bshadow-b",
  "[&>:not(:last-child)]:bshadow-gray-6",
])

// #endregion

// #region Types & Constants

const CAROUSEL_OPTIONS: Carousel.CarouselOptions = {
  loop: false,
  align: "center",
  breakpoints: {
    "(max-width: 767px)": {
      containScroll: false,
    },
    "(min-width: 768px)": {
      active: false,
    },
  },
}

export const PACKAGE_SPECIFICATIONS: Specifications = {
  title: "SPECIFICATIONS",
  items: [
    {
      title: "Width",
      value: "4.50in",
    },
    {
      title: "Height",
      value: "6.25in",
    },
  ],
}

export type PackageSlide = {
  title: string
  image: {
    src: string
    alt: string
    width: number
    height: number
  }
}

export type PackageParts = {
  title: string
  items: Array<{
    description: string
  }>
}

export type Specifications = {
  title: string
  items: Array<{
    title: string
    value: string
  }>
}

// #endregion

export const PackageDetails = {
  Root,
  Parts,
  Overview,
}
